import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdminStore } from '@/lib/admin-store';

interface Props {
  children: React.ReactNode;
}

/* ── Error Boundary ── */
class AdminErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('Admin panel error:', error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center transition-colors duration-700">
          <div className="text-center px-6">
            <h1 className="text-2xl font-syncopate font-bold tracking-tighter text-black dark:text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-[11px] text-black/40 dark:text-white/40 mb-6 max-w-md">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
              className="px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-opacity"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const AdminGuard: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, login } = useAdminStore();
  const initialized = useAdminStore((s) => s.initialized);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [shake, setShake] = useState(false);
  const [initTimeout, setInitTimeout] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Trigger initialization if not already done
  useEffect(() => {
    useAdminStore.getState().initialize();
    const timer = setTimeout(() => setInitTimeout(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Show loading while data initializes (max 5s)
  if (isAuthenticated && !initialized && !initTimeout) {
    return (
      <div className="min-h-screen bg-[#fafafa] dark:bg-[#050505] flex flex-col items-center justify-center transition-colors duration-700">
        <h1 className="text-2xl font-syncopate font-bold tracking-tighter text-black dark:text-white logo-brand mb-4">
          FOTOATELIER.
        </h1>
        <div className="w-7 h-7 border-2 border-black/10 dark:border-white/10 border-t-black dark:border-t-white rounded-full animate-spin" />
        <p className="mt-4 text-[9px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
          Loading admin data...
        </p>
      </div>
    );
  }

  if (isAuthenticated) return <AdminErrorBoundary>{children}</AdminErrorBoundary>;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await login(password);
      if (result.ok) {
        toast.success('Welcome back');
        return;
      }
      setShake(true);
      setTimeout(() => setShake(false), 500);
      toast.error(
        result.reason === 'rate_limited'
          ? 'Too many failed attempts. Wait 15 minutes and try again.'
          : 'Wrong password',
      );
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center transition-colors duration-700">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, x: shake ? [0, -10, 10, -10, 10, 0] : 0 }}
        transition={{ duration: shake ? 0.4 : 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm px-6"
      >
        <div className="text-center mb-12">
          <h1 className="text-3xl font-syncopate font-bold tracking-tighter text-black dark:text-white logo-brand">
            FOTOATELIER.
          </h1>
          <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.5em] text-black/20 dark:text-white/20">
            Admin Panel
          </p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <Lock size={14} className="text-black/20 dark:text-white/20" />
            </div>
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoFocus
              className="w-full pl-11 pr-11 py-4 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-2xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20 hover:text-black/50 dark:hover:text-white/50 transition-colors"
            >
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>

          <motion.button
            type="submit"
            disabled={submitting}
            whileHover={{ scale: submitting ? 1 : 1.01 }}
            whileTap={{ scale: submitting ? 1 : 0.98 }}
            className="w-full py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Enter
          </motion.button>
        </form>

        <p className="mt-8 text-center text-[9px] text-black/15 dark:text-white/15 font-medium">
          Gallery Fotoatelier Administration
        </p>
      </motion.div>
    </div>
  );
};

export default AdminGuard;
